// Screen readers, focus and motion. Factory-owned.
//
// Every one of these is something an app either does from the first commit or
// never does at all, because none of them are visible to the person building
// it. Nobody notices a missing focus trap unless they navigate by keyboard, and
// nobody notices a missing live region unless they cannot see the screen. So
// the chassis does them, and an app gets them by using the components rather
// than by remembering.
//
// Lifted from the patterns ADHD Moments arrived at the hard way, after it had
// already shipped without them.

import { ref } from 'vue';

/**
 * Say something to a screen reader.
 *
 * Live regions only announce a CHANGE, so the text is cleared first and set on
 * the next frame — assigning the same string twice in a row is otherwise
 * silent, which is exactly the case that matters ("Saved" after two saves).
 *
 * `assertive` interrupts whatever is being read. Reserve it for things that
 * have gone wrong; everything else waits its turn.
 */
export const politeMessage = ref('');
export const assertiveMessage = ref('');

export function announce(message, { assertive = false } = {}) {
  const target = assertive ? assertiveMessage : politeMessage;
  target.value = '';
  requestAnimationFrame(() => { target.value = message; });
}

/** Honour the OS setting. Checked at call time, because it can change. */
export function prefersReducedMotion() {
  return typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/**
 * Keep the keyboard inside an open dialog, and put it back afterwards.
 *
 * Without the trap, Tab walks out of a sheet and into the page behind it, which
 * is still there and still clickable to a keyboard even though it looks
 * covered. Without the restore, closing a sheet drops focus onto <body> and the
 * next Tab starts from the top of the document — so a screen-reader user who
 * opens a sheet from halfway down a list is returned to the beginning of the
 * page for their trouble.
 *
 * Returns a release function. Call it when the dialog closes.
 */
const FOCUSABLE = [
  'a[href]', 'button:not([disabled])', 'input:not([disabled])',
  'select:not([disabled])', 'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

export function trapFocus(container) {
  if (!container) return () => {};
  const previous = document.activeElement;

  const items = () => [...container.querySelectorAll(FOCUSABLE)]
    .filter((el) => el.offsetParent !== null || el === document.activeElement);

  const onKey = (e) => {
    if (e.key !== 'Tab') return;
    const list = items();
    if (!list.length) { e.preventDefault(); container.focus?.(); return; }
    const first = list[0];
    const last = list[list.length - 1];
    const active = document.activeElement;
    if (e.shiftKey && (active === first || active === container)) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && active === last) {
      e.preventDefault();
      first.focus();
    }
  };

  container.addEventListener('keydown', onKey);
  // The first focusable thing, or the container itself when there is none.
  (items()[0] ?? container).focus?.();

  return () => {
    container.removeEventListener('keydown', onKey);
    try { previous?.focus?.(); } catch { /* the element may be gone */ }
  };
}
